import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import type { Currencies, CurrencyFilters } from '../models';
import * as currenciesService from '../services/currencies.service';

export const useCurrencies = (defaultFilters: Partial<CurrencyFilters & { searchParams: { ids: string[] } }> = {}) => {
  const { searchParams, ...otherDefaultFilters } = useMemo(() => defaultFilters, [defaultFilters]);
  const [filters, setFilters] = useState<Partial<CurrencyFilters>>(otherDefaultFilters);

  const cacheData = currenciesService.getAll();

  const updateFilters = useCallback((newFilters: Partial<CurrencyFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const {
    data: allData,
    isLoading: allLoading,
    isError: allError,
  } = useQuery(currenciesService.currenciesQueryOptions(filters));
  const {
    data: searchData,
    isLoading: searchLoading,
    isError: searchError,
  } = useQuery(
    currenciesService.currenciesQueryOptions(
      { ...filters, search: { id: filters.search?.currencyName } },
      { enabled: Boolean(filters.search?.currencyName) },
    ),
  );

  const missingIds = useMemo(() => {
    if (!searchParams?.ids?.length) {
      return [];
    }
    const mergedMap: Currencies['map'] = {
      ...(allData?.map ?? {}),
      ...(searchData?.map ?? {}),
      ...(cacheData?.map ?? {}),
    };
    return searchParams.ids.filter((id) => !mergedMap[id]);
  }, [searchParams?.ids, allData?.map, searchData?.map, cacheData?.map]);

  const {
    data: preloadData,
    isLoading: isPreloadLoading,
    isError: isPreloadError,
  } = useQuery(
    currenciesService.currenciesQueryOptions(
      { search: { id: missingIds.join(',') } },
      { enabled: missingIds.length > 0 },
    ),
  );

  const mergedMap: Currencies['map'] = {
    ...(allData?.map ?? {}),
    ...(searchData?.map ?? {}),
    ...(cacheData?.map ?? {}),
    ...(preloadData?.map ?? {}),
  };

  return {
    data: {
      list: Object.values(mergedMap),
      map: mergedMap,
    },
    isError: allError || searchError || isPreloadError,
    isLoading: allLoading || searchLoading || isPreloadLoading,
    updateFilters,
  };
};
